import { Box, Stack, Typography, useTheme } from "@mui/material";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { getLineChartXAxisLabels } from "src/components/monitoring/axisUtils";
import { TimeRange, TimeFrame } from "src/components/monitoring/commonTypes";

interface LineBottomAxisProps {
    timeframe: TimeFrame;
    locale: Intl.Locale;
    numberOfXLabels: number;
    largerGap?: boolean;
}

export const LineBottomAxis = ({ timeframe, locale, numberOfXLabels, largerGap = false }: LineBottomAxisProps) => {
    const theme: SenecBaseTheme = useTheme();
    const xLegendValues = getLineChartXAxisLabels(timeframe, timeframe.interval as unknown as TimeRange, locale, numberOfXLabels);

    const isFirstOrLastLabel = (index: number) => index === 0 || index === xLegendValues.length - 1;
    const getLabelAlignment = (index: number) => {
        switch (index) {
            case 0:
                return "flex-start";
            case xLegendValues.length - 1:
                return "flex-end";
            default:
                return "center";
        }
    };

    return (
        <Stack
            direction={"row"}
            justifyContent={"space-between"}
            px={2}
            style={{ position: "absolute", bottom: largerGap ? "-45px" : "-35px", width: "100%" }}
        >
            {xLegendValues.map((legendValue, index) => (
                <Box
                    flex={isFirstOrLastLabel(index) ? 1 : 2}
                    key={legendValue}
                    px={isFirstOrLastLabel(index) ? 0 : "4px"}
                    style={{
                        height: "19px",
                        display: "flex",
                        justifyContent: getLabelAlignment(index),
                    }}
                    sx={{
                        "&:after": {
                            content: '""',
                            width: "3px",
                            height: "3px",
                            borderRadius: "12.5px",
                            backgroundColor: theme.palette.senecSecondary.neutralGray20,
                            display: "flex",
                            position: "absolute",
                            top: "-4px",
                            left: index === 0 ? "14px" : "unset",
                            right: index === xLegendValues.length - 1 ? "14px" : "unset",
                        },
                    }}
                >
                    <Typography
                        variant={"body1Responsive"}
                        sx={{ color: theme.palette.senecSecondary.neutralGray50 }}
                        data-testid={`line-bottom-label-${index}`}
                    >
                        {legendValue}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
};
